import { useAcombaBridgeStore } from '../../stores/acombaBridgeStore';
import { useTenantStore } from '../../stores/tenantStore';
import type { SyncType } from '../../types/sync/enums';
import type { BridgeSyncRequest } from '../../services/syncService';

/**
 * Builds the bridge sync request from the latest store state. Reads state fresh via getState()
 * (never a captured selector value) so the payload reflects the most recent extraction. Returns
 * null when extraction hasn't produced a payload or the company identity is missing — callers
 * decide how to surface that (status message, error screen, or silent skip).
 */
export function buildBridgeSyncPayload(syncType: SyncType, isCompleteRead: boolean): BridgeSyncRequest | null {
    const bridge = useAcombaBridgeStore.getState();
    const payload = bridge.syncPayload;
    if (!payload || !bridge.companyIcIdentifiantPublique) return null;

    return {
        syncType,
        companyIdentity: bridge.companyIcIdentifiantPublique,
        highWaterMark: useTenantStore.getState().launchedHighWaterMark,
        acombaVersion: bridge.bridgeInfo?.bridgeVersion ?? null,
        fetchedAt: new Date().toISOString(),
        customers: payload.customers,
        invoices: payload.invoices,
        payments: payload.payments,
        relanceAccess: bridge.functionalityAccess?.relance ?? null,
        // The company's Transphere activation from the last bridge company read, recorded backend-side so
        // the completeness banner's gate is set at sync time on desktop just as it is on the cloud path.
        transphereActive: bridge.companyTransphereActive ?? null,
        transphereEncaissementActif: bridge.companyTransphereEncaissementActif,
        transphereReceiptSubscription: bridge.companyTransphereReceiptSubscription ?? null,
        // `?? null` preserves the extraction's own verdict rather than inventing one: an older payload in the
        // store has no paymentLinks at all, and "unknown" must not be posted as "there are none".
        paymentLinks: payload.paymentLinks ?? null,
        // Comes from the extraction that produced this payload, never assumed: it is what licenses the backend
        // to read an absent customer as owing nothing and remove their relance.
        isCompleteRead,
    };
}
